import { AnimatePresence, motion } from 'framer-motion';
import {
  ChevronDown,
  Package,
  Pencil,
  Plus,
  Trash2,
  Upload,
  X,
  ZoomIn,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import AdminLayout from '../../components/admin/AdminLayout';
import { ResponsiveTable } from '../../components/admin/responsive';
import { api } from '../../services/api';

const REAIS = (c) =>
  (c / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

function comprimirImagem(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const MAX = 500;
        let { width, height } = img;
        if (width > MAX) {
          height = Math.round((height * MAX) / width);
          width = MAX;
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.82));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const FORM_VAZIO = {
  name: '',
  description: '',
  priceInCents: '',
  stock: '',
  category: '',
  coverBase64: '',
};

export default function Produtos() {
  const [produtos, setProdutos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [modalAberto, setModalAberto] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(FORM_VAZIO);
  const [preview, setPreview] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const inputFileRef = useRef();

  async function carregar() {
    setCarregando(true);
    try {
      const { data } = await api.get('/admin/produtos');
      setProdutos(data);
    } catch {
      setErro('Erro ao carregar produtos.');
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    carregar();
  }, []);

  // Fechar lightbox com ESC
  useEffect(() => {
    if (!lightbox) return;
    const fn = (e) => {
      if (e.key === 'Escape') setLightbox(null);
    };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [lightbox]);

  function abrirNovo() {
    setEditando(null);
    setForm(FORM_VAZIO);
    setPreview('');
    setErro('');
    setModalAberto(true);
  }

  function abrirEditar(p) {
    setEditando(p.id);
    setForm({
      name: p.name,
      description: p.description || '',
      priceInCents: (p.priceInCents / 100).toFixed(2),
      stock: String(p.stock),
      category: p.category || '',
      coverBase64: p.coverBase64 || '',
    });
    setPreview(p.coverBase64 || '');
    setErro('');
    setModalAberto(true);
  }

  function fecharModal() {
    setModalAberto(false);
    setEditando(null);
    setForm(FORM_VAZIO);
    setPreview('');
    setErro('');
  }

  async function handleImagem(e) {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) {
      setErro('Selecione uma imagem.');
      return;
    }
    try {
      const b64 = await comprimirImagem(file);
      setForm((f) => ({ ...f, coverBase64: b64 }));
      setPreview(b64);
      setErro('');
    } catch {
      setErro('Erro ao processar imagem.');
    }
  }

  async function salvar(e) {
    e.preventDefault();
    setErro('');
    if (!form.name || !form.priceInCents) {
      setErro('Nome e preço são obrigatórios.');
      return;
    }
    setSalvando(true);
    try {
      const priceInCents = Math.round(
        parseFloat(form.priceInCents.replace(',', '.')) * 100,
      );
      const payload = { ...form, priceInCents, stock: Number(form.stock || 0) };
      if (editando) {
        const { data } = await api.patch(
          `/admin/produtos/${editando}`,
          payload,
        );
        setProdutos((ps) => ps.map((p) => (p.id === editando ? data : p)));
      } else {
        const { data } = await api.post('/admin/produtos', payload);
        setProdutos((ps) => [data, ...ps]);
      }
      fecharModal();
    } catch (err) {
      setErro(err?.response?.data?.erro || 'Erro ao salvar.');
    } finally {
      setSalvando(false);
    }
  }

  async function deletar(id) {
    try {
      await api.delete(`/admin/produtos/${id}`);
      setProdutos((ps) => ps.filter((p) => p.id !== id));
    } catch {
      setErro('Erro ao deletar.');
    } finally {
      setConfirmDelete(null);
    }
  }

  async function toggleAtivo(produto) {
    try {
      const { data } = await api.patch(`/admin/produtos/${produto.id}`, {
        active: !produto.active,
      });
      setProdutos((ps) => ps.map((p) => (p.id === produto.id ? data : p)));
    } catch {
      setErro('Erro ao alterar status.');
    }
  }

  // Agrupar por categoria
  const grupos = produtos.reduce((acc, p) => {
    const cat = p.category || 'Sem categoria';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(p);
    return acc;
  }, {});

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-pink-50 rounded-xl flex items-center justify-center">
              <Package size={18} className="text-pink-500" />
            </div>
            <div>
              <h1 className="text-lg font-black text-slate-900">Produtos</h1>
              <p className="text-xs text-slate-400">
                {produtos.length} produto(s) · {Object.keys(grupos).length}{' '}
                setor(es)
              </p>
            </div>
          </div>
          <button
            onClick={abrirNovo}
            className="flex items-center gap-2 bg-[#ff2d6c] text-white text-xs font-black uppercase tracking-wide px-4 py-2.5 rounded-full hover:bg-[#e02059] transition-all shadow-lg shadow-pink-500/20"
          >
            <Plus size={14} /> Novo produto
          </button>
        </div>

        {erro && !modalAberto && (
          <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 text-xs text-red-600">
            {erro}
          </div>
        )}

        {/* Setores */}
        {carregando ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="h-14 bg-slate-100 rounded-2xl animate-pulse"
              />
            ))}
          </div>
        ) : produtos.length === 0 ? (
          <div className="text-center py-16 text-slate-400 text-xs">
            Nenhum produto cadastrado.
          </div>
        ) : (
          <div className="space-y-3">
            {Object.entries(grupos).map(([categoria, itens]) => (
              <Setor
                key={categoria}
                categoria={categoria}
                itens={itens}
                onEditar={abrirEditar}
                onDeletar={setConfirmDelete}
                onToggleAtivo={toggleAtivo}
                onZoom={setLightbox}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modal criar/editar */}
      {modalAberto && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 50,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
            background: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '24px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
              width: '100%',
              maxWidth: '540px',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '24px',
                borderBottom: '1px solid #f1f5f9',
              }}
            >
              <h2
                style={{
                  fontSize: 'clamp(14px, 4vw, 18px)',
                  fontWeight: 900,
                  color: '#0f172a',
                  margin: 0,
                  textTransform: 'uppercase',
                }}
              >
                {editando ? 'Editar produto' : 'Novo produto'}
              </h2>
              <button
                onClick={fecharModal}
                style={{
                  padding: '8px',
                  borderRadius: '8px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => (e.target.style.background = '#f1f5f9')}
                onMouseLeave={(e) =>
                  (e.target.style.background = 'transparent')
                }
              >
                <X size={16} style={{ color: '#64748b' }} />
              </button>
            </div>

            <form
              onSubmit={salvar}
              style={{
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}
            >
              <div>
                <label
                  style={{
                    fontSize: '10px',
                    fontWeight: 900,
                    color: '#94a3b8',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    display: 'block',
                    marginBottom: '12px',
                  }}
                >
                  Foto do produto
                </label>
                <div
                  onClick={() => inputFileRef.current?.click()}
                  style={{
                    position: 'relative',
                    aspectRatio: '4 / 5',
                    maxWidth: window.innerWidth < 480 ? '120px' : '160px',
                    margin: '0 auto',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    border: '2px dashed #e2e8f0',
                    background: '#f8fafc',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s, background-color 0.2s',
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.borderColor = '#ff2d6c')
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.borderColor = '#e2e8f0')
                  }
                >
                  {preview ? (
                    <>
                      <img
                        src={preview}
                        alt="Preview"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                      <div
                        style={{
                          position: 'absolute',
                          inset: 0,
                          background: 'rgba(0, 0, 0, 0.4)',
                          display: 'none',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        onMouseEnter={(e) => (e.style.display = 'flex')}
                        onMouseLeave={(e) => (e.style.display = 'none')}
                      >
                        <Upload size={20} style={{ color: '#fff' }} />
                      </div>
                    </>
                  ) : (
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        color: '#94a3b8',
                      }}
                    >
                      <Upload size={24} />
                      <span
                        style={{
                          fontSize: '10px',
                          textAlign: 'center',
                          padding: '0 8px',
                        }}
                      >
                        Clique para enviar
                      </span>
                    </div>
                  )}
                </div>
                <input
                  ref={inputFileRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleImagem}
                />
              </div>

              <div>
                <label
                  style={{
                    fontSize: '10px',
                    fontWeight: 900,
                    color: '#94a3b8',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    display: 'block',
                    marginBottom: '8px',
                  }}
                >
                  Nome
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, name: e.target.value }))
                  }
                  required
                  placeholder="Ex: Caneca Lily"
                  style={{
                    width: '100%',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    fontSize: '16px',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    minHeight: '44px',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => (e.target.style.borderColor = '#ff2d6c')}
                  onBlur={(e) => (e.target.style.borderColor = '#e2e8f0')}
                />
              </div>

              <div>
                <label
                  style={{
                    fontSize: '10px',
                    fontWeight: 900,
                    color: '#94a3b8',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    display: 'block',
                    marginBottom: '8px',
                  }}
                >
                  Descrição
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, description: e.target.value }))
                  }
                  rows={3}
                  placeholder="Descrição do produto..."
                  style={{
                    width: '100%',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    fontSize: '16px',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    minHeight: '100px',
                  }}
                  onFocus={(e) => (e.target.style.borderColor = '#ff2d6c')}
                  onBlur={(e) => (e.target.style.borderColor = '#e2e8f0')}
                />
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns:
                    window.innerWidth < 480 ? '1fr' : '1fr 1fr',
                  gap: '12px',
                }}
              >
                <div>
                  <label
                    style={{
                      fontSize: '10px',
                      fontWeight: 900,
                      color: '#94a3b8',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      display: 'block',
                      marginBottom: '8px',
                    }}
                  >
                    Preço (R$)
                  </label>
                  <input
                    type="text"
                    value={form.priceInCents}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, priceInCents: e.target.value }))
                    }
                    required
                    placeholder="39,90"
                    style={{
                      width: '100%',
                      border: '1px solid #e2e8f0',
                      borderRadius: '12px',
                      padding: '12px 16px',
                      fontSize: '16px',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                      minHeight: '44px',
                      boxSizing: 'border-box',
                    }}
                    onFocus={(e) => (e.target.style.borderColor = '#ff2d6c')}
                    onBlur={(e) => (e.target.style.borderColor = '#e2e8f0')}
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontSize: '10px',
                      fontWeight: 900,
                      color: '#94a3b8',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      display: 'block',
                      marginBottom: '8px',
                    }}
                  >
                    Estoque
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={form.stock}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, stock: e.target.value }))
                    }
                    placeholder="10"
                    style={{
                      width: '100%',
                      border: '1px solid #e2e8f0',
                      borderRadius: '12px',
                      padding: '12px 16px',
                      fontSize: '16px',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                      minHeight: '44px',
                      boxSizing: 'border-box',
                    }}
                    onFocus={(e) => (e.target.style.borderColor = '#ff2d6c')}
                    onBlur={(e) => (e.target.style.borderColor = '#e2e8f0')}
                  />
                </div>
              </div>

              <div>
                <label
                  style={{
                    fontSize: '10px',
                    fontWeight: 900,
                    color: '#94a3b8',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    display: 'block',
                    marginBottom: '8px',
                  }}
                >
                  Categoria / Setor
                </label>
                <input
                  type="text"
                  value={form.category}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, category: e.target.value }))
                  }
                  placeholder="Ex: Canecas, Camisas, Acessórios..."
                  style={{
                    width: '100%',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    fontSize: '16px',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    minHeight: '44px',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => (e.target.style.borderColor = '#ff2d6c')}
                  onBlur={(e) => (e.target.style.borderColor = '#e2e8f0')}
                />
              </div>

              {erro && (
                <div
                  style={{
                    fontSize: '12px',
                    color: '#dc2626',
                    background: '#fee2e2',
                    border: '1px solid #fecaca',
                    borderRadius: '8px',
                    padding: '12px',
                  }}
                >
                  {erro}
                </div>
              )}

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns:
                    window.innerWidth < 480 ? '1fr' : '1fr 1fr',
                  gap: '12px',
                  marginTop: '16px',
                }}
              >
                <button
                  type="button"
                  onClick={fecharModal}
                  style={{
                    border: '1px solid #e2e8f0',
                    color: '#64748b',
                    padding: '12px 24px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 900,
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    background: '#fff',
                    minHeight: '44px',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) => (e.target.style.background = '#f8fafc')}
                  onMouseLeave={(e) => (e.target.style.background = '#fff')}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={salvando}
                  style={{
                    background: '#ff2d6c',
                    color: '#fff',
                    padding: '12px 24px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 900,
                    textTransform: 'uppercase',
                    border: 'none',
                    cursor: salvando ? 'not-allowed' : 'pointer',
                    opacity: salvando ? 0.5 : 1,
                    minHeight: '44px',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) =>
                    !salvando && (e.target.style.background = '#e02059')
                  }
                  onMouseLeave={(e) => (e.target.style.background = '#ff2d6c')}
                >
                  {salvando
                    ? 'Salvando...'
                    : editando
                      ? 'Salvar'
                      : 'Criar produto'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmar delete */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-xs w-full text-center space-y-4">
            <Trash2 size={24} className="text-red-400 mx-auto" />
            <p className="text-sm font-bold text-slate-900">Deletar produto?</p>
            <p className="text-xs text-slate-400">
              Se já houver pedidos, o produto será apenas desativado.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="flex-1 border border-slate-200 text-slate-500 py-2.5 rounded-full text-xs font-black"
              >
                Cancelar
              </button>
              <button
                onClick={() => deletar(confirmDelete)}
                className="flex-1 bg-red-500 text-white py-2.5 rounded-full text-xs font-black hover:bg-red-600"
              >
                Deletar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
            className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.88, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.88, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 340, damping: 28 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-md w-full"
            >
              <button
                onClick={() => setLightbox(null)}
                className="absolute -top-4 -right-4 w-9 h-9 bg-white rounded-full flex items-center justify-center shadow-lg z-10 hover:bg-pink-50"
              >
                <X size={16} className="text-slate-600" />
              </button>
              <img
                src={lightbox.coverBase64}
                alt={lightbox.name}
                className="w-full rounded-2xl shadow-2xl object-contain max-h-[80vh]"
              />
              <div className="mt-3 text-center space-y-0.5">
                <p className="text-white font-black text-sm uppercase tracking-tight">
                  {lightbox.name}
                </p>
                <p className="text-white/50 text-xs">
                  {lightbox.category} · {REAIS(lightbox.priceInCents)} ·
                  Estoque: {lightbox.stock}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AdminLayout>
  );
}

/* ── Setor expansível ────────────────────────────────────────────────────── */
function Setor({
  categoria,
  itens,
  onEditar,
  onDeletar,
  onToggleAtivo,
  onZoom,
}) {
  const [aberto, setAberto] = useState(true);
  const ativos = itens.filter((p) => p.active).length;

  return (
    <div className="border border-slate-100 rounded-2xl overflow-hidden shadow-sm">
      <button
        onClick={() => setAberto((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <span className="text-xs font-black text-slate-900 uppercase tracking-widest">
            {categoria}
          </span>
          <span className="text-[9px] font-black text-slate-400 bg-white border border-slate-200 px-2 py-0.5 rounded-full">
            {itens.length} produto{itens.length !== 1 ? 's' : ''}
          </span>
          {ativos < itens.length && (
            <span className="text-[9px] font-black text-amber-600 bg-amber-50 border border-amber-100 px-2 py-0.5 rounded-full">
              {itens.length - ativos} inativo
              {itens.length - ativos !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <motion.div
          animate={{ rotate: aberto ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown size={16} className="text-slate-400" />
        </motion.div>
      </button>

      <AnimatePresence initial={false}>
        {aberto && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="p-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {itens.map((produto) => (
                <ProdutoCard
                  key={produto.id}
                  produto={produto}
                  onEditar={onEditar}
                  onDeletar={onDeletar}
                  onToggleAtivo={onToggleAtivo}
                  onZoom={onZoom}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Card do produto no admin ────────────────────────────────────────────── */
function ProdutoCard({ produto, onEditar, onDeletar, onToggleAtivo, onZoom }) {
  return (
    <div
      className={`group flex flex-col rounded-xl overflow-hidden border transition-all ${produto.active ? 'border-slate-100' : 'border-slate-100 opacity-60'}`}
    >
      {/* Imagem */}
      <div className="relative aspect-square bg-slate-50">
        {produto.coverBase64 ? (
          <>
            <img
              src={produto.coverBase64}
              alt={produto.name}
              className="w-full h-full object-cover"
            />
            <button
              onClick={() => onZoom(produto)}
              className="absolute top-2 right-2 w-7 h-7 bg-white/90 backdrop-blur-sm rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
            >
              <ZoomIn size={13} className="text-slate-600" />
            </button>
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package size={24} className="text-slate-200" />
          </div>
        )}
        {!produto.active && (
          <div className="absolute inset-0 bg-white/50 flex items-center justify-center">
            <span className="text-[8px] font-black uppercase text-slate-400 bg-white px-2 py-1 rounded-full border border-slate-200">
              Inativo
            </span>
          </div>
        )}
        {produto.stock === 0 && produto.active && (
          <div className="absolute bottom-2 left-2">
            <span className="text-[8px] font-black bg-red-500 text-white px-2 py-0.5 rounded-full">
              Esgotado
            </span>
          </div>
        )}
        {produto.stock > 0 && produto.stock <= 3 && (
          <div className="absolute bottom-2 left-2">
            <span className="text-[8px] font-black bg-amber-500 text-white px-2 py-0.5 rounded-full">
              Últimas {produto.stock}
            </span>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-2.5 flex flex-col gap-1.5 bg-white flex-1">
        <p className="text-[10px] font-black text-slate-900 uppercase tracking-tight line-clamp-2 leading-tight">
          {produto.name}
        </p>
        <p className="text-xs font-black text-slate-900">
          {REAIS(produto.priceInCents)}
        </p>
        <p className="text-[9px] text-slate-400">Estoque: {produto.stock}</p>

        {/* Ações */}
        <div className="flex gap-1 mt-auto pt-1">
          <button
            onClick={() => onToggleAtivo(produto)}
            className={`flex-1 text-[8px] font-black uppercase py-1 rounded-lg border transition-colors ${produto.active ? 'bg-green-50 text-green-600 border-green-100 hover:bg-green-100' : 'bg-slate-100 text-slate-400 border-slate-200 hover:bg-slate-200'}`}
          >
            {produto.active ? 'Ativo' : 'Inativo'}
          </button>
          <button
            onClick={() => onEditar(produto)}
            className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <Pencil size={12} />
          </button>
          <button
            onClick={() => onDeletar(produto.id)}
            className="p-1 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-400 transition-colors"
          >
            <Trash2 size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}
